import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, X, Paperclip, Flame, Clock, CheckCircle2,
  ChevronRight, User, FilterX,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { getFiresByAccount, subscribeToFires, createFire } from '../services/firebase/fires';
import { getInternalUsers } from '../services/userService';
import { filterPortalAssignable } from '../utils/portalAssignable';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { formatTimeAgo } from '../utils/timeAgo';
import PortalBackground from '../components/PortalBackground';

// Note: 'Pending' is stored in the data but shown to clients as "In Progress"
const STATUS_META = {
  Active: {
    label: 'Active',
    icon: Flame,
    tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    countText: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    ring: 'ring-2 ring-red-400 dark:ring-red-500',
  },
  Pending: {
    label: 'In Progress',
    icon: Clock,
    tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    countText: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    ring: 'ring-2 ring-amber-400 dark:ring-amber-500',
  },
  Resolved: {
    label: 'Resolved',
    icon: CheckCircle2,
    tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    countText: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    ring: 'ring-2 ring-emerald-400 dark:ring-emerald-500',
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.Active;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.badge}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const MyFiresPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const { isMobile } = useResponsive();

  // State
  const [fires, setFires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // New Fire Popup State
  const [showNewFirePopup, setShowNewFirePopup] = useState(false);
  const [fireName, setFireName] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load internal users on component mount
  useEffect(() => {
    const fetchInternalUsers = async () => {
      try {
        const users = await getInternalUsers();
        setInternalUsers(users);
      } catch (error) {
        console.error('Error fetching internal users:', error);
      }
    };

    fetchInternalUsers();
  }, []);

  // Load fires on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;
      const accountId = companyId || currentUser.uid;
      if (!accountId) return;

      try {
        setLoading(true);
        const firesData = await getFiresByAccount(accountId);
        setFires(firesData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, companyId]);

  // Set up real-time listener for fires
  useEffect(() => {
    if (!currentUser) return;
    const accountId = companyId || currentUser.uid;
    if (!accountId) return;

    const unsubscribe = subscribeToFires((firesData) => {
      // Fires for current company (shared across team members)
      const userFires = firesData;

      // Only update if we have new data and it's different from current state
      setFires(prevFires => {
        // Check if the data is actually different to avoid unnecessary updates
        const currentIds = new Set(prevFires.map(fire => fire.id));
        const newIds = new Set(userFires.map(fire => fire.id));

        // If the IDs are the same, don't update
        if (currentIds.size === newIds.size &&
            [...currentIds].every(id => newIds.has(id))) {
          return prevFires;
        }

        // Create a map of existing fires by ID for quick lookup
        const existingFiresMap = new Map(prevFires.map(fire => [fire.id, fire]));

        // Update or add fires from the real-time data
        userFires.forEach(fire => {
          existingFiresMap.set(fire.id, fire);
        });

        // Convert back to array and sort by date reported (newest first)
        const mergedFires = Array.from(existingFiresMap.values())
          .sort((a, b) => new Date(b.dateReported) - new Date(a.dateReported));

        return mergedFires;
      });
    }, accountId);

    return () => unsubscribe();
  }, [currentUser, companyId]);

  // Handle file upload
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size <= 10 * 1024 * 1024) { // 10MB limit
        setFile(selectedFile);
        setFileError('');
      } else {
        setFileError('File must be less than 10MB');
        setFile(null);
      }
    }
  };

  // Remove file
  const removeFile = () => {
    setFile(null);
    setFileError('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fireName.trim()) {
      alert('Please enter a fire name.');
      return;
    }

    if (!description.trim()) {
      alert('Please enter a description.');
      return;
    }

    if (!assigneeId) {
      alert('Please select someone to assign this fire to.');
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedUser = internalUsers.find(user => user.id === assigneeId);

      const fireData = {
        name: fireName.trim(),
        description: description.trim(),
        assignedTo: assigneeId === 'anyone' ? ['Anyone'] : [selectedUser?.name || selectedUser?.firstName + ' ' + selectedUser?.lastName],
        status: 'Active',
        deadline: null,
        dateReported: new Date().toISOString(),
        accountId: companyId || currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        isInternal: false,
        companyName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown Company'
      };

      await createFire(fireData);

      // Reset form and close popup
      setFireName('');
      setDescription('');
      setAssigneeId('');
      setFile(null);
      setShowNewFirePopup(false);

    } catch (error) {
      console.error('Error reporting fire:', error);
      alert('Failed to report fire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close popup and reset form
  const closePopup = () => {
    setShowNewFirePopup(false);
    setFireName('');
    setDescription('');
    setAssigneeId('');
    setFile(null);
    setFileError('');
  };

  // Calculate quick stats
  const quickStats = {
    Active: fires.filter(fire => fire.status === 'Active').length,
    Pending: fires.filter(fire => fire.status === 'Pending').length,
    Resolved: fires.filter(fire => fire.status === 'Resolved').length,
  };

  // Get unique values for filters
  const assignedToOptions = [...new Set(fires.flatMap(fire => fire.assignedTo).filter(Boolean))];

  const hasActiveFilters = Boolean(search || assignedFilter || statusFilter);

  const clearFilters = () => {
    setSearch('');
    setAssignedFilter('');
    setStatusFilter('');
  };

  // Filter + sort fires (newest first)
  const visibleFires = useMemo(() => {
    return fires
      .filter(fire => {
        const searchMatch = !search ||
          (fire.name?.toLowerCase() || '').includes(search.toLowerCase()) ||
          (fire.description?.toLowerCase() || '').includes(search.toLowerCase()) ||
          (fire.assignedTo?.some(assignee => assignee.toLowerCase().includes(search.toLowerCase())));

        const assignedMatch = !assignedFilter || (fire.assignedTo || []).includes(assignedFilter);
        const statusMatch = !statusFilter || fire.status === statusFilter;

        return searchMatch && assignedMatch && statusMatch;
      })
      .sort((a, b) => new Date(b.dateReported || 0) - new Date(a.dateReported || 0));
  }, [fires, search, assignedFilter, statusFilter]);

  return (
    <div className={`relative container mx-auto ${isMobile ? 'px-3 py-4' : 'px-4 py-6'} min-h-screen`}>
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
          <BackButton onClick={() => navigate('/portal')} />
        </div>
        <div className={`text-center ${isMobile ? 'px-14' : 'px-44'} py-1`}>
          <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>My Fires</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Urgent issues that need our team's attention right away.
          </p>
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
          <button
            type="button"
            onClick={() => setShowNewFirePopup(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            {!isMobile && <span>Report a Fire</span>}
          </button>
        </div>
      </div>

      {/* Status stat cards — click to filter */}
      <div className={`grid grid-cols-3 ${isMobile ? 'gap-2 mb-4' : 'gap-4 mb-6'}`}>
        {Object.entries(STATUS_META).map(([status, meta]) => {
          const Icon = meta.icon;
          const count = quickStats[status];
          const active = statusFilter === status;
          return (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(active ? '' : status)}
              title={active ? 'Clear status filter' : `Show ${meta.label.toLowerCase()} fires`}
              className={`group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${
                isMobile ? 'p-3' : 'p-4'
              } ${active ? meta.ring : ''}`}
            >
              <div className={`flex items-center ${isMobile ? 'flex-col gap-1.5 text-center' : 'gap-3'}`}>
                <div className={`${isMobile ? 'w-9 h-9' : 'w-11 h-11'} rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                  <Icon className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold leading-none ${
                    count === 0 ? 'text-gray-300 dark:text-gray-600' : meta.countText
                  }`}>
                    {loading ? '…' : count}
                  </p>
                  <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{meta.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Toolbar: search + filters */}
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-3'} mb-4`}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search your fires..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {assignedToOptions.length > 0 && (
          <select
            className={`${isMobile ? 'w-full' : 'w-48'} px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
          >
            <option value="">All Assignees</option>
            {assignedToOptions.map((assignee, idx) => (
              <option key={idx} value={assignee}>{assignee}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            <FilterX className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Fires list */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
              </div>
            </div>
          ))}
        </div>
      ) : visibleFires.length === 0 ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
          <Flame className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          {fires.length === 0 ? (
            <>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No fires reported</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                That's a good thing! If something urgent comes up, report it here and we'll jump on it.
              </p>
              <button
                type="button"
                onClick={() => setShowNewFirePopup(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" />
                Report a Fire
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No fires match your filters</p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400"
              >
                Clear filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleFires.map(fire => {
            const meta = STATUS_META[fire.status] || STATUS_META.Active;
            const Icon = meta.icon;
            return (
              <button
                key={fire.id}
                type="button"
                onClick={() => navigate(`/portal/fire/${fire.id}`)}
                className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex items-center gap-3"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{fire.name}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1 min-w-0">
                      <User className="w-3 h-3 shrink-0" />
                      <span className="truncate">{(fire.assignedTo || []).join(', ') || 'Unassigned'}</span>
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="whitespace-nowrap">{formatTimeAgo(fire.dateReported)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!isMobile && <StatusBadge status={fire.status} />}
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* New Fire Modal */}
      {showNewFirePopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closePopup}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
              <button
                type="button"
                onClick={closePopup}
                className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                  <Flame className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Report a Fire</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Flag an urgent issue so our team can jump on it.</p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Fire Name */}
              <div>
                <label htmlFor="fireName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  What's on fire? <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="fireName"
                  value={fireName}
                  onChange={(e) => setFireName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="A short headline for the issue..."
                  autoFocus
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows="5"
                  placeholder="What happened? What's the impact? Anything we should know to fix it fast?"
                  required
                />
              </div>

              {/* Assignee Selection */}
              <div>
                <label htmlFor="assigneeId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Who should handle it? <span className="text-red-500">*</span>
                </label>
                <select
                  id="assigneeId"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="" disabled>Select someone to assign this fire to...</option>
                  <option value="anyone">Anyone on the team</option>
                  {filterPortalAssignable(internalUsers).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || `${user.firstName} ${user.lastName}`}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Not sure who? Choose "Anyone on the team."
                </p>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Attachment <span className="text-gray-400 font-normal">(optional, max 10MB)</span>
                </label>
                {file ? (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <Paperclip className="w-4 h-4" />
                    Click to attach a file
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                    />
                  </label>
                )}
                {fileError && (
                  <p className="text-red-500 text-xs mt-1">{fileError}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closePopup}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !fireName.trim() || !description.trim() || !assigneeId}
                  className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Reporting...' : 'Report Fire'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MyFiresPage;
